/**
 * №13.3 §1 — the completion contract. ONE path every contextual action / create
 * form runs after its verb succeeds, so "what happens after success" is a
 * framework guarantee, not per-page wiring (the №11.1 gap that gave create-from-
 * contact no routing, no refresh, no feedback):
 *
 *   1. The originating record's related lists + queries invalidate automatically
 *      — wired through the Sprint 6.0a reactive-invalidation registry
 *      (`invalidateForTool`), the same map the in-app chat uses, so a form
 *      mutation refreshes the identical surfaces a chat mutation would.
 *   2. The action bars always re-resolve (`["context-actions"]`).
 *   3. `on_success` routes: `open` navigates to the new record; `stay` keeps the
 *      originating record in view and lands a confirmation toast (with a "View"
 *      link when the new record is resolvable). A confirmation ALWAYS lands.
 *
 * Pure orchestration — the caller supplies `navigate` + `qc`; this module owns
 * the contract so CreateEntityModal, VerbLaunchModal and ContextActionBar behave
 * identically.
 */
import type { NavigateFunction } from "react-router";
import type { QueryClient } from "@tanstack/react-query";
import { invalidateForTool } from "../../../lib/mutation-registry";
import { showToast } from "../../components/primitives";

export type OnSuccess = "open" | "stay";

/** The new record a completed action produced (its Place route target). */
export interface CompletionTarget {
  slug: string;
  id: string;
}

/**
 * The chrome default when an action doesn't declare `on_success`: a create-shaped
 * verb routes to the record it made (`open`); everything else stays put (`stay`).
 * A declared value always wins (the maker's choice per action).
 */
export function effectiveOnSuccess(verb: string, declared?: OnSuccess): OnSuccess {
  return declared ?? (/^create_/.test(verb) ? "open" : "stay");
}

/** The App Shell route for a record. */
export function placeRoute(slug: string, id: string): string {
  return `/app/place/${slug}/${id}`;
}

/**
 * Run the completion contract. Invalidate → re-resolve actions → route or confirm.
 */
export function completeAction(opts: {
  /** The verb that just succeeded — drives reactive invalidation. */
  verb: string;
  /** open | stay (already resolved via effectiveOnSuccess). */
  onSuccess: OnSuccess;
  /** The new record, when one is resolvable (enables routing / the View link). */
  target: CompletionTarget | null;
  qc: QueryClient;
  navigate: NavigateFunction;
  /** The confirmation copy shown when staying. */
  message: string;
  /** Extra react-query keys some verbs still declare (VERB_FORMS.invalidate). */
  extraInvalidate?: readonly (readonly string[])[];
}): void {
  const { verb, onSuccess, target, qc, navigate, message, extraInvalidate } = opts;

  // (1) reactive invalidation — the originating record's related lists refresh.
  invalidateForTool(verb, qc);
  for (const key of extraInvalidate ?? []) void qc.invalidateQueries({ queryKey: [...key] });
  // (2) action bars always re-resolve.
  void qc.invalidateQueries({ queryKey: ["context-actions"] });

  // (3) route (open) or confirm (stay). Open with no resolvable target falls
  //     back to a toast so feedback still lands.
  if (onSuccess === "open" && target) {
    navigate(placeRoute(target.slug, target.id));
    return;
  }
  if (target) {
    showToast(message, "success", false, {
      label: "View",
      onClick: () => navigate(placeRoute(target.slug, target.id)),
    });
  } else {
    showToast(message, "success");
  }
}
